import { api } from './api';
import { fmtAuditDateTime } from './audit-time';

/* Types dérivés du contrat tRPC plutôt que recopiés : une colonne ajoutée côté
   serveur apparaît ici sans édition, et une colonne retirée casse à la
   compilation au lieu de mentir à l'affichage. */
type ListOutput = Awaited<ReturnType<typeof api.admin.conversations.comments.list.query>>;

export type ConversationComment = ListOutput[number];
export type ConversationCommentKind = ConversationComment['kind'];

/* Les libellés vus à l'écran. Les identifiants, eux, viennent du domaine : le
   `Record` est typé sur la clôture de l'union, donc ajouter une nature côté
   serveur sans lui donner de libellé ici casse à la compilation plutôt que
   d'afficher un identifiant technique à Ismael.

   Chaque entrée vient d'un défaut RÉELLEMENT mesuré — audits du 26/08, corpus
   du harnais, signaux de l'explorateur — et le `hint` dit lequel, pour que le
   choix se fasse sur un exemple et non sur une intuition. */
export const COMMENT_KINDS: Record<ConversationCommentKind, { label: string; hint: string }> = {
  wrong_answer: {
    label: 'Réponse fausse',
    hint: "Un fait inexact, ou avancé sans avoir cherché — « le Tour de France 2025 n'a pas encore eu lieu », sans appel d'outil.",
  },
  fabricated_source: {
    label: 'Source inventée',
    hint: "Une URL ou une citation fabriquée. Pire que pas de source : l'enseignant la cite en cours.",
  },
  irrelevant_source: {
    label: 'Source hors sujet',
    hint: 'Une source réelle mais sans rapport — le référentiel qui rend de la cyberdéfense sur les nationalismes. La parade est l’ingestion, pas un garde-fou.',
  },
  announced_not_done: {
    label: 'Action annoncée, non faite',
    hint: "Le tour annonce une action et n'appelle aucun outil. Confirme ou infirme le signal « annonce sans outil ».",
  },
  false_failure: {
    label: 'Panne inventée',
    hint: "Le tour revendique une panne ou une limite d'outils qui n'a pas eu lieu. Confirme ou infirme le signal « revendique une limite ».",
  },
  incomplete_delivery: {
    label: 'Travail non livré',
    hint: "Ce qui était annoncé n'est pas livré, ou l'est à moitié — le cours annoncé et jamais produit en dix messages.",
  },
  tone_or_wording: {
    label: 'Ton ou formulation',
    hint: 'Registre, posture ou vocabulaire hors de ce que le produit dit — le lexique enseignant fait autorité sur les mots vus.',
  },
  good_example: {
    label: 'Bon exemple',
    hint: "À garder comme référence. L'amélioration continue a besoin d'exemples à conserver autant que de défauts à corriger.",
  },
  other: {
    label: 'Autre',
    hint: 'Le texte libre porte tout. Sans cette case, le reste se rangerait de force dans la plus proche et polluerait les décomptes.',
  },
};

export const COMMENT_KIND_IDS = Object.keys(COMMENT_KINDS) as ConversationCommentKind[];

/* Le seul cas positif : il se distingue à l'œil des huit défauts. */
export function isPositiveKind(kind: ConversationCommentKind): boolean {
  return kind === 'good_example';
}

export const COMMENT_MAX_LENGTH = 4000;

export async function fetchComments(threadId: string): Promise<ListOutput> {
  return api.admin.conversations.comments.list.query({ threadId });
}

export async function addComment(input: {
  threadId: string;
  messageId: string | null;
  kind: ConversationCommentKind;
  body: string;
}): Promise<ConversationComment> {
  return api.admin.conversations.comments.add.mutate(input);
}

export async function editComment(
  id: string,
  patch: { kind: ConversationCommentKind; body: string },
): Promise<ConversationComment> {
  return api.admin.conversations.comments.edit.mutate({ id, ...patch });
}

export async function removeComment(id: string): Promise<void> {
  await api.admin.conversations.comments.remove.mutate({ id });
}

/* L'auteur peut avoir quitté la plateforme : la ligne le dit alors plutôt que
   d'afficher un vide qu'on lirait comme « anonyme ». */
export function authorLabel(comment: ConversationComment): string {
  return comment.authorEmail ?? 'auteur supprimé';
}

export function fmtCommentDate(iso: string): string {
  return fmtAuditDateTime(iso);
}
